import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ShieldCheck, HeartHandshake, Users, ArrowRight, Info, Phone, Clock, AlertTriangle, FileText, DollarSign } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { clinicInfo } from "../mock";
import useSeo from "../hooks/useSeo";

const POINTS = [
  {
    icon: CheckCircle2,
    title: "No out-of-pocket cost",
    desc: "Bring your Medicare card and pay nothing at the counter. We claim the full rebate directly — you leave with $0 to pay."
  },
  {
    icon: Users,
    title: "Everyone welcome",
    desc: "Adults, children, families, pensioners and health-care-card holders — every eligible patient is bulk billed for standard consultations."
  },
  {
    icon: ShieldCheck,
    title: "No hidden fees",
    desc: "No booking fees. No admin fees. No gap fees for standard consultations. Just quality medical care, fully covered by Medicare."
  },
  {
    icon: HeartHandshake,
    title: "Focus on care, not cost",
    desc: "We believe good healthcare shouldn't depend on your bank balance. That's why we've kept our doors open to everyone in Beaudesert."
  }
];

const FAQS = [
  {
    q: "Do I really pay nothing?",
    a: "Correct — for standard consultations, you pay $0 at the clinic. Just present your Medicare card and we'll process the claim on the spot."
  },
  {
    q: "What if I don't have Medicare?",
    a: "If you're an overseas visitor or don't hold a Medicare card, please phone reception on (07) 5541 1011 before booking so we can discuss fees."
  },
  {
    q: "Are procedures also bulk billed?",
    a: "Most consultations, care plans and reviews are bulk billed. For some skin excisions, complex procedures, WorkCover or insurance medicals, an additional fee may apply — reception will always let you know before your visit."
  },
  {
    q: "Do I need a referral?",
    a: "No referral is needed to see a GP at Beaudesert General Practice. Just book online with HotDoc or call reception."
  }
];

export default function Fees() {
  useSeo({
    title: "Fees & Billing | 100% Bulk-Billing Clinic — Beaudesert General Practice",
    description: "Beaudesert General Practice is a 100% bulk-billing clinic. All standard consultations are bulk billed with a valid Medicare card. Learn what to bring and how to book.",
    path: "/fees"
  });
  return (
    <div className="relative bg-[#f0edfa] min-h-screen" style={{ overflowX: "clip" }}>
      <Navbar />

      {/* Hero */}
      <section className="relative px-4 md:px-8 pt-10 pb-16 md:pt-14 md:pb-20 molecule-bg">
        <div className="mx-auto max-w-[1200px]">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#1a2464] font-semibold text-sm mb-8 hover:text-[#3dc9b3]"
          >
            ← Back to home
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-10 items-center">
            <div>
              <span className="eyebrow">Fees & Billing</span>
              <h1 className="section-title">
                We're a <span className="text-[#3dc9b3]">100% bulk-billing</span> clinic
              </h1>
              <p className="text-[#4a4a63] mt-6 leading-relaxed text-[15px] md:text-[17px] max-w-2xl">
                Standard consultations at Beaudesert General Practice are bulk billed for every eligible Medicare patient. Bring your Medicare card and there is nothing to pay at the counter.
              </p>

              <div className="flex flex-wrap gap-3 mt-8">
                <a
                  href={clinicInfo.hotdocUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2 bg-[#1a2464] text-white font-semibold pl-6 pr-2 py-2 rounded-full hover:bg-[#141c52]"
                >
                  Book on HotDoc
                  <span className="w-9 h-9 rounded-full bg-[#3dc9b3] grid place-items-center group-hover:translate-x-0.5 transition-transform">
                    <ArrowRight className="w-4 h-4 text-white" />
                  </span>
                </a>
                <a
                  href={`tel:${clinicInfo.phoneRaw}`}
                  className="inline-flex items-center gap-2 bg-[#3dc9b3] text-white font-semibold px-5 py-3 rounded-full hover:bg-[#34b39f]"
                >
                  Call {clinicInfo.phone}
                </a>
              </div>
            </div>

            {/* Big $0 card */}
            <div className="relative rounded-[32px] bg-[#1a2464] text-white overflow-hidden p-10 text-center">
              <div className="absolute inset-0 wave-pattern opacity-60 pointer-events-none" />
              <div className="relative">
                <div className="text-white/60 text-xs uppercase tracking-[0.25em] mb-4">You pay at the counter</div>
                <div className="text-[7rem] md:text-[9rem] font-extrabold leading-none text-[#3dc9b3]">$0</div>
                <div className="text-white/80 mt-4 text-sm">for standard consultations with a valid Medicare card</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Points */}
      <section className="px-4 md:px-8 pb-16 md:pb-24">
        <div className="mx-auto max-w-[1200px] grid grid-cols-1 md:grid-cols-2 gap-5">
          {POINTS.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="bg-white rounded-3xl p-7 border border-[#eee6f7] shadow-sm">
                <div className="w-12 h-12 rounded-2xl bg-[#3dc9b3]/15 grid place-items-center mb-4">
                  <Icon className="w-6 h-6 text-[#3dc9b3]" />
                </div>
                <div className="font-bold text-[#1a2464] text-lg">{p.title}</div>
                <p className="text-[#4a4a63] text-[15px] leading-relaxed mt-2">{p.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* About your appointment */}
      <section id="about-your-appointment" className="px-4 md:px-8 pb-16 md:pb-24">
        <div className="mx-auto max-w-[1200px] rounded-[32px] bg-white border border-[#eee6f7] shadow-sm p-8 md:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 items-start mb-8">
            <div>
              <span className="eyebrow">Booking info</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a2464] tracking-tight">About your appointment</h2>
              <p className="text-[#4a4a63] leading-relaxed mt-4 max-w-2xl">
                Everything you need to know before your visit — from booking to what to bring, and when a private fee may apply.
              </p>
            </div>
            <a
              href={`tel:${clinicInfo.phoneRaw}`}
              className="inline-flex items-center gap-2 bg-[#3dc9b3] text-white font-semibold px-5 py-3 rounded-full hover:bg-[#34b39f]"
            >
              <Phone className="w-4 h-4" /> {clinicInfo.phone}
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <AptItem icon={<Phone className="w-5 h-5" />}>
              <strong className="text-[#1a2464]">To book</strong> — please call <a href={`tel:${clinicInfo.phoneRaw}`} className="text-[#3dc9b3] font-semibold hover:underline">{clinicInfo.phone}</a>. Walk-ins are also seen.
            </AptItem>

            <AptItem icon={<FileText className="w-5 h-5" />}>
              <strong className="text-[#1a2464]">New patients</strong> — please print and fill in the New Patient Form before coming. Otherwise, arrive 5 minutes early and we'll provide the form to fill in at the practice.
            </AptItem>

            <AptItem icon={<Clock className="w-5 h-5" />}>
              <strong className="text-[#1a2464]">Long appointments</strong> — if you need more than 30 minutes with your doctor, please advise reception so we can book you in for a longer time.
            </AptItem>

            <AptItem icon={<AlertTriangle className="w-5 h-5" />} tone="urgent">
              <strong className="text-[#1a2464]">Emergencies</strong> will always be seen as a priority.
            </AptItem>

            <AptItem icon={<CheckCircle2 className="w-5 h-5" />} tone="highlight" wide>
              <strong className="text-[#1a2464]">100% Bulk Billing</strong> — Beaudesert General Practice is 100% bulk billing for all Medicare rebatable consultations.
            </AptItem>

            <AptItem icon={<Info className="w-5 h-5" />}>
              <strong className="text-[#1a2464]">Not registered with Medicare?</strong> Private fees are payable at the time of consultation for medical examinations, insurance reports and after-hours visits to home or hospital.
            </AptItem>

            <AptItem icon={<DollarSign className="w-5 h-5" />}>
              <strong className="text-[#1a2464]">Repeat prescriptions</strong> — a $10 script fee applies for repeat prescriptions (only if approved by your GP; not applicable to Authority scripts).
            </AptItem>
          </div>
        </div>
      </section>
      <section className="px-4 md:px-8 pb-16 md:pb-24">
        <div className="mx-auto max-w-[1200px] rounded-[32px] bg-white border border-[#eee6f7] shadow-sm p-8 md:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <span className="eyebrow">Before your visit</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a2464] tracking-tight">What to bring</h2>
              <p className="text-[#4a4a63] mt-4 leading-relaxed">
                A few simple things help us process your visit quickly and keep everything bulk billed:
              </p>
            </div>
            <ul className="space-y-4">
              {[
                "Your Medicare card (physical or in the Express Plus app)",
                "Any Pension, Health Care or DVA card if you have one",
                "A list of current medications and known allergies",
                "Referrals or reports from other doctors, if relevant"
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <span className="mt-0.5 shrink-0 w-6 h-6 rounded-full bg-[#3dc9b3]/15 grid place-items-center">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#3dc9b3]" />
                  </span>
                  <span className="text-[#1a1a2e] text-[15px]">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-4 md:px-8 pb-20 md:pb-28">
        <div className="mx-auto max-w-[1200px]">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="eyebrow">Frequently asked</span>
            <h2 className="section-title">Fees & billing questions</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {FAQS.map((f) => (
              <div key={f.q} className="bg-white rounded-2xl p-6 border border-[#eee6f7]">
                <div className="flex items-start gap-3">
                  <span className="shrink-0 w-8 h-8 rounded-full bg-[#1a2464] grid place-items-center">
                    <Info className="w-4 h-4 text-[#3dc9b3]" />
                  </span>
                  <div>
                    <div className="font-bold text-[#1a2464]">{f.q}</div>
                    <p className="text-[#4a4a63] text-sm leading-relaxed mt-2">{f.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function AptItem({ icon, children, tone = "default", wide = false }) {
  const toneClasses = {
    default: "bg-[#f0edfa] text-[#3dc9b3]",
    urgent: "bg-[#fde4e4] text-[#c92f2f]",
    highlight: "bg-[#3dc9b3] text-white"
  };
  const border =
    tone === "highlight"
      ? "border-[#3dc9b3]"
      : tone === "urgent"
      ? "border-[#c92f2f]/20"
      : "border-[#eee6f7]";
  return (
    <div
      className={`flex items-start gap-4 rounded-2xl bg-white border ${border} p-5 ${wide ? "md:col-span-2" : ""}`}
    >
      <span
        className={`shrink-0 w-11 h-11 rounded-2xl grid place-items-center ${toneClasses[tone]}`}
      >
        {icon}
      </span>
      <p className="text-[#4a4a63] text-[15px] leading-relaxed">{children}</p>
    </div>
  );
}

